import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode'; // Import jwtDecode
import styles from './BusinessPortfolioDetail.module.css';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { Carousel } from 'react-responsive-carousel';
import { ArrowLeft, Home, MapPin, Edit2 } from 'lucide-react'; // Added Icons
import ModernNavbarPureCSS from "../../components/Navbar/ModernNavBarPureCSS"; // Import Navbar

const BusinessPortfolioDetail = () => {
    const { portfolioId } = useParams();
    const navigate = useNavigate();
    const [portfolio, setPortfolio] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [username, setUsername] = useState(''); // State for Navbar

    const businessId = localStorage.getItem('loggedInBusinessId');

    useEffect(() => {
        // 1. Get Username for Navbar
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const decodeToken = jwtDecode(token);
                setUsername(decodeToken.UserName || 'User');
            } catch (e) {
                console.error("Token decode error", e);
            }
        }

        // 2. Fetch Data
        const fetchPortfolioDetail = async () => {
            setIsLoading(true);
            try {
                const response = await axios.get(`${API_BASE_URL}/api/WorkPortfolio/GetWorkPortfolioDetail?portfolioId=${portfolioId}&businessId=${businessId}`);
                if (response.data && response.data.model) {
                    setPortfolio(response.data.model);
                }
            } catch (err) {
                setError('Failed to fetch portfolio details.');
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        };

        if (portfolioId && businessId) {
            fetchPortfolioDetail();
        } else {
            setError("Portfolio ID or Business ID is missing.");
            setIsLoading(false);
        }
    }, [portfolioId, businessId]);

    // --- HELPER: Fix Relative Image Paths ---
    const getMediaUrl = (url) => {
        if (!url) return 'https://via.placeholder.com/800x500?text=No+Image';
        
        // If absolute URL, return as is
        if (url.startsWith('http') || url.startsWith('https')) {
            return url;
        }

        // If relative, prepend API_BASE_URL
        const baseUrl = API_BASE_URL.replace(/\/$/, '');
        const cleanPath = url.startsWith('/') ? url : `/${url}`;
        return `${baseUrl}${cleanPath}`;
    };

    if (isLoading) return <p style={{textAlign:'center', marginTop:'5rem'}}>Loading...</p>;
    if (error) return <p className={styles.error}>{error}</p>;
    if (!portfolio) return <p className={styles.error}>Portfolio not found.</p>;

    return (
        <div className={styles.pageContainer}>
            
            {/* 1. Add Navbar */}
            <ModernNavbarPureCSS UserName={username} prefix={''} TabActiveId={'portfolios'} />

            {/* 2. Gold Header Bar */}
            <header className={styles.header}>
                <div className={styles.headerContent}>
                    <div className={styles.headerLeft}>
                        <button onClick={() => navigate(-1)} className={styles.btnGhost}>
                            <ArrowLeft size={24} />
                        </button>
                        
                        {/* Title Block */}
                        <div>
                            <h1 className={styles.headerTitle}>{portfolio.title}</h1>
                        </div>
                    </div>

                    <Link to={`/business-portfolios/edit/${portfolio.portfolioId}`} className={styles.btnGold}>
                        <Edit2 size={18} /> Edit Portfolio
                    </Link>
                </div>
            </header>

            <main className={styles.mainContainer}>
                <div className={styles.detailCard}>
                    
                    {/* Location */}
                    <div className={styles.locationRow}>
                        <MapPin size={20} color="#d97706" />
                        <span>{portfolio.suburb}, {portfolio.postcode}</span>
                    </div>

                    {/* Description */}
                    <div className={styles.description}>
                        <p>{portfolio.description}</p>
                    </div>

                    {/* Carousel with Fixed Image Paths */}
                    {portfolio.mediaUrl && portfolio.mediaUrl.length > 0 && (
                        <div className={`${styles.carouselContainer} ${styles.customCarousel}`}>
                            <Carousel showThumbs={false} infiniteLoop useKeyboardArrows showArrows={true}>
                                {portfolio.mediaUrl.map((media) => (
                                    <div key={media.id}>
                                        <img 
                                            src={getMediaUrl(media.url)} 
                                            alt={portfolio.title} 
                                            onError={(e) => {
                                                e.target.onerror = null; 
                                                e.target.src='https://via.placeholder.com/800x500?text=Error+Loading+Image';
                                            }}
                                        />
                                    </div>
                                ))}
                            </Carousel>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default BusinessPortfolioDetail;